import { AddressLinkDropdownPreview } from '@Components/AddressLinkDropdownPreview';
import { LinkedFieldWithLayoutSettings } from '@Components/LinkedFieldWithLayoutSettings';
import { EditSetting } from '@Components/Settings';
import { DOCUMENTS_API, PERMISSIONS_API, STAMMDATEN_API } from '@Constants/backendConstants';
import { getFrameLabel } from '@Constants/getFrameLabel';
import { Permission } from '@Constants/Permission.g';
import { addressColumns, paymentTermsColumns } from '@Constants/TableColumns';
import { useHasPermission } from '@Contexts/appContext';
import { useSettings } from '@Hooks/Settings';
import {
    type ApiAddress,
    type ApiMandant,
    type ApiPaymentTerms,
    EnumDocumentSettingGroups,
    EnumDocumentSettingModuleName,
    EnumFrontendLayoutSetting,
    EnumPaymentMethod,
    EnumStammdatenSettingGroups,
    EnumStammdatenSettingModuleName,
    EnumStammdatenSettings,
} from '@Model';
import { FrameEditAddress, FrameEditPaymentTerms } from '@Modules/Stammdaten';
import {
    Card,
    EditFrameMode,
    EditFrameShell,
    EnumFilterExpressionOperation,
    EnumOperation,
    FilterCondition,
    FlexRow,
    FrameEditBase,
    Grid,
    GridColumns,
    type IFrameBodyProps,
    type IFrameEditShellProps,
    isOk,
    ListRequest,
    Result,
    TextInput,
    useFrameEditBaseNavigation,
    useGuiBinder,
    useLinkedFieldInFrame,
    useLinkedObjectOverlay,
    useToasts,
    useTranslate,
} from '@synqo/frontend-component-library';

import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import { useDynamicExportOptions } from '@Hooks/Exports';
import { DynamicExportCategory, DynamicExportKey } from '@Constants/dynamicExportConstants';
import { useListApi } from '@Hooks/useListApi';
import { PaymentTermsRepository } from '@Repositories/Stammdaten';

export function FrameEditMandant(props: IFrameEditShellProps<ApiMandant>) {
    const hasPermission = useHasPermission();
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    return (
        <FrameEditBase<ApiMandant>
            apiResourcePath={`${PERMISSIONS_API}/mandanten`}
            renderer={FrameEditMandantBody}
            key={primaryKey}
            primaryKey={primaryKey}
            embedded={props.embedded || props.primaryKey !== undefined}
            canEdit={props.canEdit !== false && hasPermission(Permission.PermissionsMandantenUpdate)}
            canCreate={false}
            canDelete={false}
            frameProps={{ events: { ...frameEditBaseNavigationEvents, ...props.events } }}
        />
    );
}

function FrameEditMandantBody(props: IFrameBodyProps<ApiMandant>) {
    const i18n = useTranslate('');
    const { pushAllToasts } = useToasts();
    const { hasExportOptions } = useDynamicExportOptions(DynamicExportCategory.Invoices, DynamicExportKey.Accounting);

    const binder = useGuiBinder<ApiMandant>({
        ...props,
        canEdit: props.mode !== EditFrameMode.View,
    });

    const listPaymentTermsApi = useListApi(PaymentTermsRepository, STAMMDATEN_API);
    const loadCashPaymentTerms = (listRequest: ListRequest) => {
        const cashOrCard: FilterCondition[] = [
            {
                field: 'PaymentMethod',
                value: EnumPaymentMethod.Cash.toString(),
                operator: EnumOperation.Equal,
            },
            {
                field: 'PaymentMethod',
                value: EnumPaymentMethod.Card.toString(),
                operator: EnumOperation.Equal,
            },
        ];
        if (listRequest.filter_columns === undefined) {
            listRequest.filter_columns = { operation: EnumFilterExpressionOperation.And, filters: [] };
        }

        listRequest.filter_columns.filters.push({ operation: EnumFilterExpressionOperation.And, filters: cashOrCard });
        return listPaymentTermsApi.loadPage(listRequest);
    };

    const stammdatenSettingModule = EnumStammdatenSettingModuleName.Addresses;
    const {
        settings: stammdatenSettings,
        updateSettingLocally: updateStammdatenSettingLocally,
        updateSettings: updateStammdatenSettings,
    } = useSettings(`${STAMMDATEN_API}/settings`, undefined, stammdatenSettingModule);

    const documentsSettingModule = EnumDocumentSettingModuleName.AccountingExport;
    const {
        settings: documentsSettings,
        updateSettingLocally: updateDocumentSettingLocally,
        updateSettings: updateDocumentSettings,
    } = useSettings(`${DOCUMENTS_API}/settings`, undefined, documentsSettingModule);

    const simpleSettings = useMemo(() => {
        const countersSettings = stammdatenSettings.filter(
            (s) => s.setting_group === EnumStammdatenSettingGroups.Counters,
        );
        const printSettings = stammdatenSettings.filter(
            (s) => s.setting_group === EnumStammdatenSettingGroups.PrintOptions,
        );

        return [...countersSettings, ...printSettings];
    }, [stammdatenSettings]);

    const paymentSettings = useMemo(
        () => ({
            expensePaymentTermsSetting: stammdatenSettings.find(
                (s) => s.setting_name === EnumStammdatenSettings.PaymentTermsForExpenses,
            ),
            revenuePaymentTermsSetting: stammdatenSettings.find(
                (s) => s.setting_name === EnumStammdatenSettings.PaymentTermsForRevenue,
            ),
            cashPaymentTermsSetting: stammdatenSettings.find(
                (s) => s.setting_name === EnumStammdatenSettings.PaymentTermsForCashPayment,
            ),
        }),
        [stammdatenSettings],
    );

    const updateRevenueSetting = useCallback(
        (_: string, linked: ApiPaymentTerms | undefined) => {
            if (paymentSettings.revenuePaymentTermsSetting === undefined) {
                return false;
            }
            updateStammdatenSettingLocally({
                ...paymentSettings.revenuePaymentTermsSetting,
                value: linked?.id ?? '',
                display_string: linked?.display_string ?? '',
            });
            return true;
        },
        [paymentSettings.revenuePaymentTermsSetting, updateStammdatenSettingLocally],
    );
    const updateExpenseSetting = useCallback(
        (_: string, linked: ApiPaymentTerms | undefined) => {
            if (paymentSettings.expensePaymentTermsSetting === undefined) {
                return false;
            }
            updateStammdatenSettingLocally({
                ...paymentSettings.expensePaymentTermsSetting,
                value: linked?.id ?? '',
                display_string: linked?.display_string ?? '',
            });
            return true;
        },
        [paymentSettings.expensePaymentTermsSetting, updateStammdatenSettingLocally],
    );
    const updateCashTermsSetting = useCallback(
        (_: string, linked: ApiPaymentTerms | undefined) => {
            if (paymentSettings.cashPaymentTermsSetting === undefined) {
                return false;
            }
            updateStammdatenSettingLocally({
                ...paymentSettings.cashPaymentTermsSetting,
                value: linked?.id ?? '',
                display_string: linked?.display_string ?? '',
            });
            return true;
        },
        [paymentSettings.cashPaymentTermsSetting, updateStammdatenSettingLocally],
    );

    const headerProps = useMemo(() => {
        return {
            label: getFrameLabel('Mandant', props.mode),
            resourceLabel: 'Mandant',
            ...props.events,
            onAdd: undefined,
        };
    }, [props.events, props.mode]);

    const handleResult = useCallback(
        <T,>(result: Result<T>) => {
            if (!isOk(result)) {
                pushAllToasts(result.messages);
                return false;
            }
            return true;
        },
        [pushAllToasts],
    );

    const bannerProps = useMemo(() => {
        const onSave = props.events.onSave;
        return {
            mode: props.mode,
            loading: props.stateQueueDepth > 0,
            ...props.events,
            onSave: onSave
                ? async () => {
                      const updateStammdatenSettingsSuccess = await updateStammdatenSettings().then(handleResult);
                      const updateDocumentsSettingsSuccess = await updateDocumentSettings().then(handleResult);
                      if (updateDocumentsSettingsSuccess && updateStammdatenSettingsSuccess) {
                          return onSave();
                      }
                      return false;
                  }
                : undefined,
        };
    }, [
        props.events,
        props.mode,
        props.stateQueueDepth,
        updateStammdatenSettings,
        handleResult,
        updateDocumentSettings,
    ]);

    const linkAddress = useLinkedFieldInFrame(props, 'address', FrameEditAddress);

    const linkedPaymentTermOverlayEvents = useLinkedObjectOverlay({
        Renderer: FrameEditPaymentTerms,
        canAdd: false,
    });

    const accountingSettings = useMemo(
        () => documentsSettings.filter((s) => s.setting_group === EnumDocumentSettingGroups.AccountingExport),
        [documentsSettings],
    );

    return (
        <EditFrameShell key={props.mode} headerProps={headerProps} bannerProps={bannerProps}>
            <Card name="PrimaryData" border="withoutBorder">
                <Grid>
                    <GridColumns spans={[4, 4, 4]}>
                        <TextInput {...binder.bindToProp<string>('name')} label={i18n('Name')} />
                        <TextInput {...binder.bindToProp<string>('number')} label={i18n('Nummer')} />
                        <LinkedFieldWithLayoutSettings<ApiAddress>
                            layoutSettingsContext={EnumFrontendLayoutSetting.AddressesTableLayout}
                            {...binder.bindToLinkProperty(linkAddress.linkApi)}
                            {...linkAddress.events}
                            columns={addressColumns}
                            label={i18n('Adresse')}
                            dropdownPreview={AddressLinkDropdownPreview}
                        />
                    </GridColumns>
                </Grid>
            </Card>
            <Card name="settings" label={i18n('Einstellungen')}>
                {simpleSettings.map((setting) => (
                    <FlexRow key={`${setting.setting_group}_${setting.setting_name}`}>
                        <EditSetting
                            setting={setting}
                            locked={props.mode === EditFrameMode.View}
                            onChange={updateStammdatenSettingLocally}
                        />
                    </FlexRow>
                ))}
                <Card
                    name={'payment_terms_settings'}
                    label={'Standard Zahlungsbedingungen'}
                    border={'withoutBorder'}
                    collapsable={false}
                >
                    <Grid>
                        <GridColumns spans={[4, 4, 4]}>
                            {paymentSettings.expensePaymentTermsSetting && (
                                <LinkedFieldWithLayoutSettings<ApiPaymentTerms>
                                    key={`${paymentSettings.expensePaymentTermsSetting.setting_group}_${paymentSettings.expensePaymentTermsSetting.setting_name}`}
                                    layoutSettingsContext={EnumFrontendLayoutSetting.PaymentTermsTableLayout}
                                    name="payment_terms_for_expenses"
                                    loadLinkableObjects={listPaymentTermsApi.loadPage}
                                    columns={paymentTermsColumns}
                                    label={paymentSettings.expensePaymentTermsSetting.label}
                                    value={paymentSettings.expensePaymentTermsSetting.display_string ?? ''}
                                    linked_key={paymentSettings.expensePaymentTermsSetting.value ?? ''}
                                    onConfirm={updateExpenseSetting}
                                    {...linkedPaymentTermOverlayEvents}
                                    locked={props.mode === EditFrameMode.View}
                                />
                            )}
                            {paymentSettings.revenuePaymentTermsSetting && (
                                <LinkedFieldWithLayoutSettings<ApiPaymentTerms>
                                    key={`${paymentSettings.revenuePaymentTermsSetting.setting_group}_${paymentSettings.revenuePaymentTermsSetting.setting_name}`}
                                    layoutSettingsContext={EnumFrontendLayoutSetting.PaymentTermsTableLayout}
                                    name="payment_terms_for_revenue"
                                    loadLinkableObjects={listPaymentTermsApi.loadPage}
                                    columns={paymentTermsColumns}
                                    label={paymentSettings.revenuePaymentTermsSetting.label}
                                    value={paymentSettings.revenuePaymentTermsSetting.display_string ?? ''}
                                    linked_key={paymentSettings.revenuePaymentTermsSetting.value ?? ''}
                                    onConfirm={updateRevenueSetting}
                                    {...linkedPaymentTermOverlayEvents}
                                    locked={props.mode === EditFrameMode.View}
                                />
                            )}
                            {paymentSettings.cashPaymentTermsSetting && (
                                <LinkedFieldWithLayoutSettings<ApiPaymentTerms>
                                    key={`${paymentSettings.cashPaymentTermsSetting.setting_group}_${paymentSettings.cashPaymentTermsSetting.setting_name}`}
                                    layoutSettingsContext={EnumFrontendLayoutSetting.PaymentTermsTableLayout}
                                    name="payment_terms_for_revenue"
                                    loadLinkableObjects={loadCashPaymentTerms}
                                    columns={paymentTermsColumns}
                                    label={paymentSettings.cashPaymentTermsSetting.label}
                                    value={paymentSettings.cashPaymentTermsSetting.display_string ?? ''}
                                    linked_key={paymentSettings.cashPaymentTermsSetting.value ?? ''}
                                    onConfirm={updateCashTermsSetting}
                                    {...linkedPaymentTermOverlayEvents}
                                    locked={props.mode === EditFrameMode.View}
                                />
                            )}
                        </GridColumns>
                    </Grid>
                </Card>
                {hasExportOptions && (
                    <Card
                        name={'accounting_export_settings'}
                        label={'FiBu Export'}
                        border={'withoutBorder'}
                        collapsable={true}
                    >
                        <Grid>
                            <GridColumns spans={[4, 4, 4]}>
                                {accountingSettings.map((setting) => (
                                    <EditSetting
                                        key={`${setting.setting_group}_${setting.setting_name}`}
                                        setting={setting}
                                        locked={props.mode === EditFrameMode.View}
                                        onChange={updateDocumentSettingLocally}
                                    />
                                ))}
                            </GridColumns>
                        </Grid>
                    </Card>
                )}
            </Card>
        </EditFrameShell>
    );
}
